import { useState, useMemo } from 'react';
import { useApp } from '../AppContext';
import { supabase } from '../supabaseClient';
import { CATEGORIES, fmt, inputStyle } from '../utils';
import { findRecurringDuplicates } from '../lib/findDuplicates';
import DuplicatesPanel from './DuplicatesPanel';

export default function RecurringTab() {
  const { recurring, setRecurring, properties, business, importBatches, runBackfill, transactions } = useApp();

  const [showRecurringForm, setShowRecurringForm] = useState(false);
  const [editingRecurring, setEditingRecurring] = useState(null);
  // start_date = the date the recurring was created with (immutable history).
  // next_due_date = the date the NEXT post will fire (advances on every backfill).
  // The form edits start_date; next_due_date is derived on insert and left
  // alone on edit so we don't accidentally re-fire historical postings.
  const [recurringForm, setRecurringForm] = useState({ property_id: '', description: '', amount: '', type: 'income', category: '', frequency: 'monthly', day_of_month: 1, start_date: '', end_date: '' });
  const [recurFilterProp, setRecurFilterProp] = useState('all');
  const [recurFilterType, setRecurFilterType] = useState('all');
  const [recurFilterFreq, setRecurFilterFreq] = useState('all');
  const [showRecurringBatches, setShowRecurringBatches] = useState(false);

  // Delete dialog state. Shape: { schedules: [...], label, futureCount, totalCount } | null
  // Single delete + batch delete both open this modal so the user gets the
  // same Future-only / All choice in both cases.
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Post-edit dialog: when the user edits a recurring and changes a field
  // that affects past-posted transactions (amount, description, category,
  // property_id), ask whether to also rewrite matching past rows.
  // Shape: { oldR, newR, matchingIds: [], matchingCount } | null
  const [editConfirm, setEditConfirm] = useState(null);
  const [applyingEdit, setApplyingEdit] = useState(false);

  const [showDuplicates, setShowDuplicates] = useState(false);
  const duplicatePairs = useMemo(() => {
    if (!showDuplicates) return [];
    return findRecurringDuplicates(recurring);
  }, [recurring, showDuplicates]);

  const displayRecurring = useMemo(() => {
    let rows = [...recurring];
    if (recurFilterProp !== 'all') rows = rows.filter(r => r.property_id === recurFilterProp);
    if (recurFilterType !== 'all') rows = rows.filter(r => r.type === recurFilterType);
    if (recurFilterFreq !== 'all') rows = rows.filter(r => r.frequency === recurFilterFreq);
    return rows;
  }, [recurring, recurFilterProp, recurFilterType, recurFilterFreq]);

  const recurringBatches = useMemo(() => {
    const grouped = {};
    recurring.filter(r => r.batch_id).forEach(r => {
      if (!grouped[r.batch_id]) grouped[r.batch_id] = [];
      grouped[r.batch_id].push(r);
    });
    return Object.entries(grouped).map(([id, items]) => {
      const txBatch = importBatches.find(b => b.id === id);
      return {
        id,
        count: items.length,
        importedAt: txBatch?.importedAt || items[0].created_at || null,
      };
    }).sort((a, b) => (b.importedAt || '').localeCompare(a.importedAt || ''));
  }, [recurring, importBatches]);

  function openAddForm() {
    setEditingRecurring(null);
    setRecurringForm({ property_id: properties[0]?.id || '', description: '', amount: '', type: 'income', category: '', frequency: 'monthly', day_of_month: 1, start_date: '', end_date: '' });
    setShowRecurringForm(true);
  }

  function openEditForm(r) {
    setEditingRecurring(r);
    // Legacy rows without start_date (pre-migration or backfill-skipped) fall
    // back to next_due_date so the field is never empty on edit.
    setRecurringForm({
      property_id: r.property_id || 'all',
      description: r.description,
      amount: Math.abs(r.amount),
      type: r.type,
      category: r.category || '',
      frequency: r.frequency,
      day_of_month: r.day_of_month,
      start_date: r.start_date || r.next_due_date || '',
      end_date: r.end_date || '',
    });
    setShowRecurringForm(true);
  }

  function closeForm() {
    setShowRecurringForm(false);
    setEditingRecurring(null);
  }

  async function saveRecurring(e) {
    e.preventDefault();
    const propertyId = recurringForm.property_id === 'all' ? null : recurringForm.property_id;
    const amount = parseFloat(recurringForm.amount) * (recurringForm.type === 'expense' ? -1 : 1);

    if (editingRecurring) {
      // Edit: write start_date (and other editable fields) but leave
      // next_due_date alone — it's an independent runtime cursor maintained
      // by api/post-recurring.js. Touching it could re-fire historical
      // postings the user didn't ask for.
      const oldR = editingRecurring;
      const updatePayload = {
        property_id: propertyId,
        description: recurringForm.description,
        amount,
        type: recurringForm.type,
        category: recurringForm.category || null,
        frequency: recurringForm.frequency,
        day_of_month: parseInt(recurringForm.day_of_month),
        start_date: recurringForm.start_date || null,
        end_date: recurringForm.end_date || null,
      };
      // Diagnostic logging — captures the FULL roundtrip so a "1000 saved
      // as 997" report can be debugged without guessing. Remove after
      // bug 2 is identified.
      console.log('[saveRecurring/edit] form.amount =', recurringForm.amount,
        '| type =', recurringForm.type,
        '| parsed amount sent =', amount,
        '| schedule id =', oldR.id);
      const { data: newR, error } = await supabase
        .from('recurring_transactions')
        .update(updatePayload)
        .eq('id', oldR.id)
        .select()
        .single();
      if (error) { alert('Error saving: ' + error.message); return; }
      console.log('[saveRecurring/edit] DB returned amount =', newR?.amount,
        '| matches sent =', Number(newR?.amount) === Number(amount));
      setRecurring(prev => prev.map(r => r.id === oldR.id ? newR : r));
      closeForm();

      // Always show the dialog on edit save — UX consistency. Previously
      // we gated on "did a past-affecting field change AND did strict
      // matching find past rows," but both checks fail silently on
      // schedules that have drifted from their historical postings.
      // Showing the dialog every time gives the user predictable
      // interaction; the dialog itself reports how many past rows we
      // can confidently rewrite.
      const matchingIds = findMatchingPastTransactionIds(oldR, transactions);
      setEditConfirm({ oldR, newR, matchingIds, matchingCount: matchingIds.length });
      return; // runBackfill happens after the user picks an option
    } else {
      // Insert: start_date and next_due_date both seeded with the same
      // value so the backfill knows where to start firing from.
      const insertPayload = {
        business_id: business.id,
        property_id: propertyId,
        description: recurringForm.description,
        amount,
        type: recurringForm.type,
        category: recurringForm.category || null,
        frequency: recurringForm.frequency,
        day_of_month: parseInt(recurringForm.day_of_month),
        start_date: recurringForm.start_date,
        next_due_date: recurringForm.start_date,
        end_date: recurringForm.end_date || null,
        active: true,
      };
      console.log('[saveRecurring/add] form.amount =', recurringForm.amount,
        '| type =', recurringForm.type,
        '| parsed amount sent =', amount);
      const { data, error } = await supabase.from('recurring_transactions').insert([insertPayload]).select().single();
      if (error) { alert('Error saving: ' + error.message); return; }
      console.log('[saveRecurring/add] DB returned amount =', data?.amount,
        '| matches sent =', Number(data?.amount) === Number(amount));
      setRecurring(prev => [...prev, data]);
      closeForm();
      runBackfill();
    }
  }

  // Called from the post-edit dialog when the user picks "Update all".
  async function applyEditToPastTransactions() {
    if (!editConfirm) return;
    const { newR, matchingIds } = editConfirm;
    setApplyingEdit(true);
    try {
      const { error } = await supabase.from('transactions').update({
        description: newR.description,
        amount: newR.amount,
        type: newR.type,
        category: newR.category,
        property_id: newR.property_id,
      }).in('id', matchingIds);
      if (error) { alert('Error updating past transactions: ' + error.message); return; }
      setEditConfirm(null);
      await runBackfill();
    } finally {
      setApplyingEdit(false);
    }
  }

  // ── Past-transaction matching ──────────────────────────────────────────
  // Permissive soft-match: property + amount within 5% + same sign. We
  // dropped the description requirement because schedules whose
  // descriptions have been edited over time no longer match their own
  // historical postings — the prior strict match would return zero rows
  // and the Update-all dialog would silently skip itself. Amount-close
  // is robust to a single-event rename and distinguishes between e.g.
  // "Rent" and "Electric" on the same property.
  function findMatchingPastTransactionIds(schedule, txs) {
    const refAmount = Number(schedule.amount);
    return txs.filter(t => {
      if (t.source !== 'recurring') return false;
      if ((t.property_id || null) !== (schedule.property_id || null)) return false;
      const txAmount = Number(t.amount);
      if (Math.sign(txAmount) !== Math.sign(refAmount)) return false;
      const ref = Math.max(Math.abs(txAmount), Math.abs(refAmount));
      if (ref === 0) return txAmount === refAmount;
      return Math.abs(txAmount - refAmount) / ref <= 0.05;
    }).map(t => t.id);
  }

  // ── Delete-with-cleanup helpers ────────────────────────────────────────
  // Transactions don't carry a FK back to their recurring schedule (the
  // post-recurring API only stamps `source: 'recurring'`). To find a
  // schedule's children we soft-match on description + amount + property +
  // source. This is exact for any normal setup; the only failure mode is
  // two schedules with IDENTICAL description+amount on the same property,
  // which would also have been indistinguishable to a human reader.

  function isChildOf(r, t) {
    return t.source === 'recurring'
      && t.description === r.description
      && Number(t.amount) === Number(r.amount)
      && (t.property_id || null) === (r.property_id || null);
  }

  function countChildren(schedules) {
    const today = new Date().toISOString().slice(0, 10);
    let total = 0, future = 0;
    for (const r of schedules) {
      for (const t of transactions) {
        if (!isChildOf(r, t)) continue;
        total++;
        if (t.transaction_date && t.transaction_date > today) future++;
      }
    }
    return { total, future };
  }

  function openDeleteDialog(schedules, label) {
    const { total, future } = countChildren(schedules);
    setDeleteDialog({ schedules, label, totalCount: total, futureCount: future });
  }

  async function performDelete(mode) {
    if (!deleteDialog) return;
    const { schedules } = deleteDialog;
    const today = new Date().toISOString().slice(0, 10);
    setDeleting(true);
    try {
      // Step 1: wipe matching transactions per schedule. We do one DELETE
      // per schedule rather than one big OR-query so the soft-match
      // criteria stay straightforward.
      for (const r of schedules) {
        let q = supabase.from('transactions').delete()
          .eq('source', 'recurring')
          .eq('business_id', r.business_id)
          .eq('description', r.description)
          .eq('amount', r.amount);
        q = r.property_id ? q.eq('property_id', r.property_id) : q.is('property_id', null);
        if (mode === 'future') q = q.gt('transaction_date', today);
        const { error } = await q;
        if (error) console.error('[recurring delete] tx cleanup error:', error.message);
      }

      // Step 2: delete the recurring schedule rows themselves
      const ids = schedules.map(s => s.id);
      const { error: schedErr } = await supabase.from('recurring_transactions').delete().in('id', ids);
      if (schedErr) console.error('[recurring delete] schedule delete error:', schedErr.message);

      // Step 3: refresh everything dashboard reads from
      await runBackfill();
      setDeleteDialog(null);
    } finally {
      setDeleting(false);
    }
  }

  function renderForm(keyId) {
    const isEditing = editingRecurring !== null;
    return (
      <form key={keyId} onSubmit={saveRecurring}
        style={{ background: '#0f1117', border: `1px solid ${isEditing ? '#3b82f6' : '#1e2235'}`, borderRadius: 12, padding: 16, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {isEditing && (
          <div style={{ fontSize: 11, color: '#3b82f6', fontFamily: "'Courier New', monospace", letterSpacing: 1, marginBottom: 4 }}>EDITING RECURRING</div>
        )}
        <select value={recurringForm.property_id} onChange={e => setRecurringForm(v => ({ ...v, property_id: e.target.value }))} style={inputStyle}>
          <option value="all">All Properties (shared expense)</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <input placeholder="Description" value={recurringForm.description} onChange={e => setRecurringForm(v => ({ ...v, description: e.target.value }))} required style={inputStyle} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <select value={recurringForm.type} onChange={e => setRecurringForm(v => ({ ...v, type: e.target.value }))} style={inputStyle}>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
          </select>
          <input type="number" placeholder="Amount" value={recurringForm.amount} onChange={e => setRecurringForm(v => ({ ...v, amount: e.target.value }))} required style={inputStyle} />
        </div>
        <select value={recurringForm.category} onChange={e => setRecurringForm(v => ({ ...v, category: e.target.value }))} style={inputStyle}>
          <option value="">Income / Rent</option>
          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <select value={recurringForm.frequency} onChange={e => setRecurringForm(v => ({ ...v, frequency: e.target.value }))} style={inputStyle}>
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
            <option value="yearly">Yearly</option>
          </select>
          <input type="number" placeholder="Day of month" min="1" max="31" value={recurringForm.day_of_month} onChange={e => setRecurringForm(v => ({ ...v, day_of_month: e.target.value }))} style={inputStyle} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>Start date:</div>
            <input type="date" value={recurringForm.start_date} onChange={e => setRecurringForm(v => ({ ...v, start_date: e.target.value }))} required style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>End date (optional):</div>
            <input type="date" value={recurringForm.end_date || ''} onChange={e => setRecurringForm(v => ({ ...v, end_date: e.target.value }))} style={inputStyle} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <button type="submit" style={{ background: '#1d4ed8', border: 'none', borderRadius: 10, color: '#fff', cursor: 'pointer', fontSize: 15, padding: '12px 0', fontWeight: 600 }}>
            {isEditing ? 'Save Changes' : 'Save'}
          </button>
          <button type="button" onClick={closeForm} style={{ background: '#1e2235', border: 'none', borderRadius: 10, color: '#94a3b8', cursor: 'pointer', fontSize: 15, padding: '12px 0' }}>Cancel</button>
        </div>
      </form>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: "'Courier New', monospace", letterSpacing: 1 }}>RECURRING TRANSACTIONS</div>
        <button onClick={openAddForm} style={{ background: '#1d4ed8', border: 'none', borderRadius: 10, padding: '10px 16px', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
          + Add Recurring
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <select value={recurFilterProp} onChange={e => setRecurFilterProp(e.target.value)}
          style={{ ...inputStyle, flex: '1 1 auto', minWidth: 120, fontSize: 13, padding: '8px 10px' }}>
          <option value="all">All Properties</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={recurFilterType} onChange={e => setRecurFilterType(e.target.value)}
          style={{ ...inputStyle, flex: '1 1 auto', minWidth: 110, fontSize: 13, padding: '8px 10px' }}>
          <option value="all">All Types</option>
          <option value="income">Income</option>
          <option value="expense">Expense</option>
        </select>
        <select value={recurFilterFreq} onChange={e => setRecurFilterFreq(e.target.value)}
          style={{ ...inputStyle, flex: '1 1 auto', minWidth: 110, fontSize: 13, padding: '8px 10px' }}>
          <option value="all">All Frequencies</option>
          <option value="monthly">Monthly</option>
          <option value="weekly">Weekly</option>
          <option value="yearly">Yearly</option>
        </select>
      </div>

      <button onClick={() => setShowDuplicates(v => !v)}
        style={{ width: '100%', marginBottom: 12, background: showDuplicates ? '#1d4ed8' : 'transparent', border: '1px solid #2d3555', borderRadius: 10, padding: '10px 0', color: showDuplicates ? '#fff' : '#94a3b8', cursor: 'pointer', fontSize: 13, fontFamily: "'Courier New', monospace", letterSpacing: 0.5 }}>
        🔍 {showDuplicates ? `HIDE DUPLICATES (${duplicatePairs.length})` : 'FIND DUPLICATES'}
      </button>

      {showDuplicates && (
        <DuplicatesPanel
          pairs={duplicatePairs}
          emptyLabel="No duplicate schedules. Each recurring is unique."
          onDelete={async (r) => {
            if (!window.confirm(`Stop and delete this recurring schedule?\n\n"${r.description}" — ${fmt(r.amount)} ${r.frequency}\n\nPast transactions it created will stay in your books.`)) return;
            const { error } = await supabase.from('recurring_transactions').delete().eq('id', r.id);
            if (!error) {
              setRecurring(prev => prev.filter(x => x.id !== r.id));
              runBackfill();
            } else { alert('Error deleting: ' + error.message); }
          }}
          renderCard={(r, onDelete) => (
            <div style={{ background: '#0a0d16', border: '1px solid #1e2235', borderRadius: 8, padding: 10 }}>
              <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: "'Courier New', monospace", marginBottom: 4 }}>
                {r.frequency} · day {r.day_of_month}
              </div>
              <div style={{ fontSize: 14, color: '#e2e8f0', fontWeight: 600, marginBottom: 4, wordBreak: 'break-word' }}>
                {r.description || '(no description)'}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
                {properties.find(p => p.id === r.property_id)?.name || 'Shared / All Properties'}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8, fontStyle: r.category ? 'normal' : 'italic' }}>
                {r.category || 'no category'}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: r.amount >= 0 ? '#4ade80' : '#f87171' }}>
                  {fmt(r.amount)}
                </div>
                <button onClick={onDelete}
                  style={{ background: '#7f1d1d', border: '1px solid #f87171', borderRadius: 6, padding: '4px 10px', color: '#f87171', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                  ✕ Delete
                </button>
              </div>
            </div>
          )}
        />
      )}

      {/* Top form: only when ADDING (not editing — edit form appears inline below) */}
      {showRecurringForm && !editingRecurring && renderForm('add-form')}

      {recurringBatches.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <button onClick={() => setShowRecurringBatches(v => !v)}
            style={{ background: '#0f1117', border: '1px solid #1e2235', borderRadius: showRecurringBatches ? '10px 10px 0 0' : 10, padding: '10px 14px', width: '100%', textAlign: 'left', color: '#94a3b8', cursor: 'pointer', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>📥 Import Batches ({recurringBatches.length})</span>
            <span style={{ fontSize: 11 }}>{showRecurringBatches ? '▲' : '▼'}</span>
          </button>
          {showRecurringBatches && (
            <div style={{ background: '#0f1117', border: '1px solid #1e2235', borderTop: 'none', borderRadius: '0 0 10px 10px' }}>
              {recurringBatches.map(batch => (
                <div key={batch.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderTop: '1px solid #1e2235' }}>
                  <div>
                    <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600 }}>{batch.count} recurring schedule{batch.count !== 1 ? 's' : ''}</div>
                    <div style={{ fontSize: 11, color: '#4b5563' }}>Imported {batch.importedAt ? new Date(batch.importedAt).toLocaleDateString() : 'from file'}</div>
                  </div>
                  <button onClick={() => {
                    const schedules = recurring.filter(r => r.batch_id === batch.id);
                    openDeleteDialog(schedules, `Import batch — ${schedules.length} schedule${schedules.length !== 1 ? 's' : ''}`);
                  }} style={{ background: '#7f1d1d', border: '1px solid #f87171', borderRadius: 8, padding: '7px 12px', color: '#f87171', cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    🗑️ Delete Batch
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {displayRecurring.length === 0 && (
          <div style={{ color: '#94a3b8', fontSize: 14, padding: '20px 0' }}>
            {recurring.length === 0 ? 'No recurring transactions set up yet.' : 'No recurring transactions match these filters.'}
          </div>
        )}
        {displayRecurring.map(r => {
          // Inline edit: swap the card for the form when this row is being edited
          if (editingRecurring?.id === r.id) {
            return renderForm(r.id);
          }
          return (
            <div key={r.id} style={{ background: '#0f1117', border: '1px solid #1e2235', borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <div style={{ fontSize: 15, color: '#e2e8f0', fontWeight: 600, flex: 1, marginRight: 12 }}>{r.description}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ fontSize: 17, fontWeight: 700, color: r.amount >= 0 ? '#4ade80' : '#f87171', whiteSpace: 'nowrap' }}>{fmt(r.amount)}</div>
                  <button onClick={() => openDeleteDialog([r], r.description)}
                    style={{ background: 'transparent', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 16, padding: '0 4px' }} title="Delete">✕</button>
                </div>
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>{r.property_id ? properties.find(p => p.id === r.property_id)?.name : 'All Properties'}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10, fontFamily: "'Courier New', monospace" }}>
                {r.frequency} · next: {r.next_due_date}{r.end_date ? ` · ends: ${r.end_date}` : ''}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => openEditForm(r)}
                  style={{ background: '#1e2235', border: '1px solid #2d3555', borderRadius: 8, padding: '6px 14px', color: '#94a3b8', cursor: 'pointer', fontSize: 12 }}>Edit</button>
                <button onClick={async () => {
                  if (!window.confirm('Stop this recurring transaction? It will no longer generate future entries.')) return;
                  await supabase.from('recurring_transactions').update({ active: false }).eq('id', r.id);
                  setRecurring(prev => prev.filter(x => x.id !== r.id));
                  runBackfill();
                }} style={{ background: '#1e2235', border: '1px solid #7f1d1d', borderRadius: 8, padding: '6px 14px', color: '#f87171', cursor: 'pointer', fontSize: 12 }}>Stop</button>
              </div>
            </div>
          );
        })}
      </div>

      {deleteDialog && (
        <div
          onClick={() => !deleting && setDeleteDialog(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#0f1117', border: '1px solid #1e2235', borderRadius: 14, padding: 20, maxWidth: 460, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
            <div style={{ fontSize: 10, color: '#f87171', fontFamily: "'Courier New', monospace", letterSpacing: 1.2, marginBottom: 6 }}>⚠ DELETE RECURRING</div>
            <div style={{ fontSize: 16, color: '#e2e8f0', fontWeight: 600, marginBottom: 4 }}>{deleteDialog.label}</div>
            <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.55, marginBottom: 16 }}>
              This schedule has posted <strong style={{ color: '#e2e8f0' }}>{deleteDialog.totalCount}</strong> transaction{deleteDialog.totalCount !== 1 ? 's' : ''} so far ({deleteDialog.futureCount} dated in the future). What do you want to do with them?
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                onClick={() => performDelete('future')}
                disabled={deleting}
                style={{ background: '#1e2235', border: '1px solid #2d3555', borderRadius: 10, padding: '12px 14px', color: '#e2e8f0', cursor: deleting ? 'wait' : 'pointer', textAlign: 'left' }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Stop future postings only</div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, lineHeight: 1.4 }}>
                  Deletes the schedule {deleteDialog.futureCount > 0 ? `and ${deleteDialog.futureCount} future transaction${deleteDialog.futureCount !== 1 ? 's' : ''}` : '(no future transactions exist yet)'}. Past entries stay in your books.
                </div>
              </button>

              <button
                onClick={() => performDelete('all')}
                disabled={deleting}
                style={{ background: '#1e2235', border: '1px solid #7f1d1d', borderRadius: 10, padding: '12px 14px', color: '#f87171', cursor: deleting ? 'wait' : 'pointer', textAlign: 'left' }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Delete everything from this schedule</div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, lineHeight: 1.4 }}>
                  Wipes the schedule plus all {deleteDialog.totalCount} transaction{deleteDialog.totalCount !== 1 ? 's' : ''} it generated (past and future). Can't be undone.
                </div>
              </button>

              <button
                onClick={() => setDeleteDialog(null)}
                disabled={deleting}
                style={{ background: 'transparent', border: '1px solid #1e2235', borderRadius: 10, padding: '10px 14px', color: '#94a3b8', cursor: deleting ? 'wait' : 'pointer', marginTop: 4, fontSize: 13 }}>
                {deleting ? 'Working…' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editConfirm && (
        <div
          onClick={() => !applyingEdit && setEditConfirm(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#0f1117', border: '1px solid #1e2235', borderRadius: 14, padding: 20, maxWidth: 460, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
            <div style={{ fontSize: 10, color: '#3b82f6', fontFamily: "'Courier New', monospace", letterSpacing: 1.2, marginBottom: 6 }}>✎ RECURRING UPDATED</div>
            <div style={{ fontSize: 16, color: '#e2e8f0', fontWeight: 600, marginBottom: 4 }}>{editConfirm.newR.description}</div>
            <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.55, marginBottom: 16 }}>
              {editConfirm.matchingCount > 0
                ? <>The schedule has been updated. This recurring has posted <strong style={{ color: '#e2e8f0' }}>{editConfirm.matchingCount}</strong> past transaction{editConfirm.matchingCount !== 1 ? 's' : ''}. What should happen to them?</>
                : <>The schedule has been updated. No past transactions from this recurring were found that match closely enough to rewrite automatically.</>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                onClick={() => { setEditConfirm(null); runBackfill(); }}
                disabled={applyingEdit}
                style={{ background: '#1e2235', border: '1px solid #2d3555', borderRadius: 10, padding: '12px 14px', color: '#e2e8f0', cursor: applyingEdit ? 'wait' : 'pointer', textAlign: 'left' }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Future only</div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, lineHeight: 1.4 }}>
                  {editConfirm.matchingCount > 0
                    ? 'New postings will use the updated values. Past transactions keep their original amount, description, and category.'
                    : 'New postings will use the updated values. Nothing else changes.'}
                </div>
              </button>

              <button
                onClick={applyEditToPastTransactions}
                disabled={applyingEdit || editConfirm.matchingCount === 0}
                style={{
                  background: '#1e2235',
                  border: '1px solid ' + (editConfirm.matchingCount > 0 ? '#1d4ed8' : '#1e2235'),
                  borderRadius: 10, padding: '12px 14px',
                  color: editConfirm.matchingCount > 0 ? '#93c5fd' : '#475569',
                  cursor: applyingEdit ? 'wait' : (editConfirm.matchingCount > 0 ? 'pointer' : 'not-allowed'),
                  textAlign: 'left',
                  opacity: editConfirm.matchingCount > 0 ? 1 : 0.5,
                }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  Update all existing transactions{editConfirm.matchingCount > 0 ? ` (${editConfirm.matchingCount})` : ''}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, lineHeight: 1.4 }}>
                  {editConfirm.matchingCount > 0
                    ? `Rewrite all ${editConfirm.matchingCount} past transaction${editConfirm.matchingCount !== 1 ? 's' : ''} from this schedule to match the new values. Useful for correcting a long-standing mistake. Can't be undone.`
                    : 'No matching past transactions to update. (Match: same property + amount within 5%. Heavily edited schedules may drift out of range.)'}
                </div>
              </button>

              <button
                onClick={() => setEditConfirm(null)}
                disabled={applyingEdit}
                style={{ background: 'transparent', border: '1px solid #1e2235', borderRadius: 10, padding: '10px 14px', color: '#94a3b8', cursor: applyingEdit ? 'wait' : 'pointer', marginTop: 4, fontSize: 13 }}>
                {applyingEdit ? 'Working…' : 'Decide later'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
